// Jeton du lien personnel (AgDR-LOCAL-0001). Le lien est `<origine>/i#<jeton>` :
// le jeton voyage dans le fragment, que le navigateur n'envoie jamais au serveur
// (ni journaux, ni en-tête Referer). Il est gardé sur l'appareil seulement.
import { ecrireSessionSw } from './session-sw';

const CLE = 'sensa-rappel.jeton';
const CLE_NOM_PILULE = 'sensa-rappel.nom-pilule';
const CLE_TEST = 'sensa-rappel.test-recu';
const CLE_PERTE_PROCHE_VUE = 'sensa-rappel.perte-proche-vue';

/** Même format que l'api : 32 octets aléatoires en base64url (43 caractères). */
export const FORMAT_JETON = /^[A-Za-z0-9_-]{43}$/;

/** Les 43 premiers caractères du jeton ; ce qui suit est ignoré. */
const DEBUT_JETON = /^[A-Za-z0-9_-]{43}/;

/**
 * Tire le jeton d'un fragment (`#…`), d'un lien complet collé ou du jeton seul.
 * Les caractères en trop après les 43 du jeton (point final d'un message, mot
 * collé au lien, espace ou retour à la ligne) sont ignorés : un mauvais jeton
 * est refusé par l'api, qui affiche alors « Ce lien n'est plus valable ». Un
 * jeton de moins de 43 caractères reste refusé ici.
 */
export function extraireJeton(saisie: string): string | null {
  const texte = saisie.trim();
  const brut = texte.includes('#') ? texte.slice(texte.lastIndexOf('#') + 1) : texte;
  return DEBUT_JETON.exec(brut)?.[0] ?? null;
}

function lire(cle: string): string | null {
  try {
    return localStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string | null): void {
  try {
    if (valeur === null) localStorage.removeItem(cle);
    else localStorage.setItem(cle, valeur);
  } catch {
    // Stockage refusé (navigation privée) : le jeton ne survivra pas à l'onglet.
  }
}

let memoire: string | null = null;

export function jetonCourant(): string | null {
  const j = lire(CLE) ?? memoire;
  return j && FORMAT_JETON.test(j) ? j : null;
}

export function enregistrerJeton(jeton: string): void {
  memoire = jeton;
  ecrire(CLE, jeton);
}

/** « Tout effacer » : rien ne reste sur l'appareil. */
export function oublierTout(): void {
  memoire = null;
  ecrire(CLE, null);
  ecrire(CLE_NOM_PILULE, null);
  ecrire(CLE_TEST, null);
  ecrire(CLE_PERTE_PROCHE_VUE, null);
  // Copie du jeton donnée au service worker (session-sw.ts).
  void ecrireSessionSw(null).catch(() => undefined);
}

/** Le nom de la pilule reste sur le téléphone : jamais envoyé au serveur (05, « Données »). */
export function nomPilule(): string {
  return lire(CLE_NOM_PILULE) ?? '';
}

export function enregistrerNomPilule(nom: string): void {
  ecrire(CLE_NOM_PILULE, nom.trim() === '' ? null : nom.trim());
}

/** La notification de test a été reçue sur cet appareil (parcours, étape 3). */
export function testRecu(): boolean {
  return lire(CLE_TEST) === '1';
}

export function noterTestRecu(): void {
  ecrire(CLE_TEST, '1');
}

/**
 * Elle a lu l'avertissement « le proche ne reçoit plus les alertes » pour
 * cette perte (#41) : une perte se dit une fois ; une perte suivante, avec sa
 * propre date, se dit de nouveau.
 */
export function perteProcheVue(perduLe: Date): boolean {
  return lire(CLE_PERTE_PROCHE_VUE) === perduLe.toISOString();
}

export function noterPerteProcheVue(perduLe: Date): void {
  ecrire(CLE_PERTE_PROCHE_VUE, perduLe.toISOString());
}
